import type { ChangeEvent, FocusEvent, KeyboardEvent, MouseEvent, ReactNode, RefObject } from 'react';
import { createRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AutocompleteText } from '../components/Autocomplete';

export const SEARCH_OVERRIDE_VALUE = '<SEARCH_OVERRIDE_VALUE>';

// Real browsers never dispatch a `click` event for a non-primary button (right-click fires
// `contextmenu`, not `click`), so this only guards against a synthetic/programmatic click
// dispatched with a non-zero `button` value - it does not affect real right-click behavior.
export function isPrimaryButtonClick(event: MouseEvent<HTMLElement>) {
  return event.button === 0;
}
export type AutocompleteOptionType = {
  description?: string;
  disabled?: boolean;
  highlightedLabel?: ReactNode;
  label: string;
  value: string;
};

export type AutocompleteBaseContextProps = {
  descriptionOrientation?: 'horizontal' | 'vertical';
  isLoading?: boolean;
  onBlur?: (event: FocusEvent<HTMLElement>) => void;
  onCloseAutoFocus?: (event: Event) => void;
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  onFocus?: (event: FocusEvent<HTMLElement>) => void;
  onOpenChange?: (open: boolean) => void;
  onPointerDownOutside?: (event: PointerEvent) => void;
  onInputChange?: (value: string) => void;
  onInputSubmit?: (searchString: string) => void;
  disableDefaultNoResults?: boolean;
  /** Configuration for component default text content, labels, placeholders, and screen reader announcements. */
  text?: AutocompleteText;
  disableTextHighlighting?: boolean;
};

export type AutocompleteBaseContextState<T extends AutocompleteOptionType> = {
  clearSearchStringIfNoValueMatches: () => void;
  getOptionByValue: (value: string) => T | undefined;
  handleInputOnBlur: (event: FocusEvent<HTMLInputElement>) => void;
  handleInputOnChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleInputOnClear: () => void;
  handleInputOnClick: (event: MouseEvent<HTMLInputElement>) => void;
  handleInputOnFocus: (event: FocusEvent<HTMLInputElement>) => void;
  handleInputOnKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  handleInputSubmit: (value?: string) => void;
  handleOpenChange: (open: boolean) => void;
  handleOptionOnClick: (event: MouseEvent<HTMLDivElement>, value: string) => void;
  handleOptionOnKeyDown: (event: KeyboardEvent<HTMLDivElement>, index: number) => void;
  handleOptionOnMouseEnter: (event: MouseEvent<HTMLDivElement>, index: number) => void;
  handleTriggerOnBlur: (event: FocusEvent<HTMLButtonElement>) => void;
  handleTriggerOnClick: (event: MouseEvent<HTMLButtonElement>) => void;
  handleTriggerOnFocus: (event: FocusEvent<HTMLButtonElement>) => void;
  inputRef: RefObject<HTMLInputElement | null>;
  isControlled: boolean;
  open: boolean;
  optionRefs: RefObject<HTMLDivElement | null>[];
  options: Map<string, T>;
  registerOption: (option: T) => void;
  searchString: string;
  setSearchString: (searchString: string) => void;
  unregisterOption: (value: string) => void;
};

export type AutocompleteBaseProviderProps = {
  children: ReactNode;
  descriptionOrientation?: 'horizontal' | 'vertical';
  disabled?: boolean;
  isLoading?: boolean;
  onBlur?: (event: FocusEvent<HTMLElement>) => void;
  onCloseAutoFocus?: (event: Event) => void;
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  onFocus?: (event: FocusEvent<HTMLElement>) => void;
  onOpenChange?: (open: boolean) => void;
  onPointerDownOutside?: (event: PointerEvent) => void;
  onInputChange?: (value: string) => void;
  readOnly?: boolean;
  disableDefaultNoResults?: boolean;
  /** Configuration for component default text content, labels, placeholders, and screen reader announcements. */
  text?: AutocompleteText;
  disableTextHighlighting?: boolean;
  inputRef: RefObject<HTMLInputElement | null>;
};

export function getOptionByValue<T extends AutocompleteOptionType>(
  value: string,
  options: Map<string, T>,
  optionLabelCache: RefObject<Record<string, string>>,
): T | undefined {
  const option = options.get(value);
  if (option) {
    optionLabelCache.current[value] = option.label;
    return option;
  }

  if (optionLabelCache.current[value]) {
    return {
      value,
      label: optionLabelCache.current[value],
      disabled: false,
      highlightedLabel: optionLabelCache.current[value],
    } as T;
  }

  const optionElements = Array.from(document.querySelectorAll(`[data-value="${value}"]`));
  if (optionElements.length > 0 && optionElements[0] && optionElements[0].textContent) {
    const label = optionElements[0].textContent;

    optionLabelCache.current[value] = label;

    return {
      value,
      label,
      disabled: false,
      highlightedLabel: label,
    } as T;
  }

  if (value) {
    optionLabelCache.current[value] = value;
    return {
      value,
      label: value,
      disabled: false,
      highlightedLabel: value,
    } as T;
  }

  return undefined;
}

function createAutocompleteSearchUtils({
  allowFreeText,
  isEnabled,
  onInputChange,
  options,
  inputValue: providedInputValue,
}: {
  allowFreeText?: boolean;
  isEnabled: boolean;
  onInputChange?: (value: string) => void;
  options?: Map<string, AutocompleteOptionType>;
  inputValue?: string;
}) {
  const [searchString, setSearchString] = useState<string>('');
  const inputValue = useMemo(() => providedInputValue ?? searchString, [providedInputValue, searchString]);

  const handleSearchStringChange = useCallback(
    (newSearchString: string) => {
      if (isEnabled) {
        setSearchString(newSearchString);
        onInputChange?.(newSearchString);
      }
    },
    [isEnabled, onInputChange],
  );

  const clearSearchStringIfNoValueMatches = useCallback(() => {
    if (!allowFreeText && inputValue && options && !Array.from(options.values()).some((option) => option.value === inputValue)) {
      setSearchString('');
      onInputChange?.('');
    }
  }, [allowFreeText, inputValue, options]);

  return {
    searchString: inputValue,
    setSearchString,
    handleSearchStringChange,
    clearSearchStringIfNoValueMatches,
  };
}

function createAutocompleteOpenUtils(
  isEnabled: boolean,
  onOpenChange?: (open: boolean) => void,
  inputRef?: RefObject<HTMLInputElement | null>,
  onBlur?: (event: FocusEvent<HTMLButtonElement>) => void,
  controlledOpen?: boolean,
) {
  const [internalOpen, setInternalOpen] = useState(false);

  const isControlledOpen = controlledOpen !== undefined;
  const open = isControlledOpen ? controlledOpen : internalOpen;

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (isEnabled) {
        if (!isControlledOpen) {
          setInternalOpen(newOpen);
        }
        onOpenChange?.(newOpen);
      }

      // custom logic to handle blur when closing the autocomplete when the input is not focused due to focus trapping in the autocomplete
      if (inputRef?.current && !inputRef.current.contains(document.activeElement)) {
        onBlur?.({
          currentTarget: inputRef.current,
          target: inputRef.current,
          type: 'blur',
          bubbles: true,
        } as unknown as FocusEvent<HTMLButtonElement>);
      }
    },
    [isEnabled, onOpenChange, isControlledOpen],
  );

  const setOpen = useCallback(
    (newOpen: boolean) => {
      if (!isControlledOpen) {
        setInternalOpen(newOpen);
      }
      onOpenChange?.(newOpen);
    },
    [isControlledOpen, onOpenChange],
  );

  return {
    open,
    setOpen,
    handleOpenChange,
    isControlledOpen,
  };
}

function createAutocompleteInputHandlerUtils(
  isEnabled: boolean,
  handleOpenChange: (open: boolean) => void,
  handleSearchStringChange: (searchString: string) => void,
  optionRefs: RefObject<HTMLDivElement | null>[],
  open?: boolean,
  onBlur?: (event: FocusEvent<HTMLButtonElement>) => void, // onBlur is technically on the trigger but is handled here
  onFocus?: (event: FocusEvent<HTMLButtonElement>) => void, // onFocus is technically on the trigger but is handled here
  onInputSubmit?: (searchString: string) => void,
  searchString?: string,
  isControlledOpen?: boolean,
  setHighlightedOption?: (index: number) => void,
  getHighlightedOption?: () => AutocompleteOptionType | null,
  clearHighlightedOption?: () => void,
  handleValueChange?: (value: string) => void,
) {
  const handleInputOnClick = useCallback(
    (event: MouseEvent<HTMLInputElement>) => {
      if (isEnabled && isPrimaryButtonClick(event)) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    [isEnabled],
  );

  const handleInputOnChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (isEnabled) {
        event.preventDefault();
        const targetValue = event.currentTarget.value;
        handleSearchStringChange(targetValue);

        // The dropdown otherwise only opens from explicit triggers elsewhere (a click on
        // the input or the trigger button, or the Up/Down arrow keys) - none of which fire
        // for a value that arrives via paste (e.g. the right-click context menu) or a
        // change event with no preceding click.
        if (!open && !isControlledOpen) {
          handleOpenChange(true);
        }
      }
    },
    [isEnabled, handleSearchStringChange, open, isControlledOpen, handleOpenChange],
  );

  const handleInputOnBlur = useCallback(
    (event: FocusEvent<HTMLInputElement, Element>) => {
      if (!open) {
        // on blur event technically fires on the trigger (a button)
        onBlur?.({
          ...event,
          currentTarget: event.currentTarget as unknown as HTMLButtonElement,
          target: event.target as unknown as HTMLButtonElement,
        } as FocusEvent<HTMLButtonElement>);
        clearHighlightedOption?.();
      }
    },
    [open, onBlur, clearHighlightedOption],
  );

  const handleInputOnClear = useCallback(() => {
    if (isEnabled) {
      handleSearchStringChange('');
      clearHighlightedOption?.();
    }
  }, [isEnabled, handleSearchStringChange, clearHighlightedOption]);

  const handleInputOnFocus = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      onFocus?.({
        ...event,
        currentTarget: event.currentTarget as unknown as HTMLButtonElement,
        target: event.target as unknown as HTMLButtonElement,
      } as FocusEvent<HTMLButtonElement>);
    },
    [onFocus, setHighlightedOption],
  );

  const handleInputOnKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (isEnabled) {
        switch (event.key) {
          case 'Enter': {
            event.preventDefault();

            const highlightedOption = getHighlightedOption?.();
            if (highlightedOption) {
              const highlightedOptionRef = optionRefs.find((ref) => ref.current?.getAttribute('data-value') === highlightedOption.value);
              if (highlightedOptionRef?.current) {
                highlightedOptionRef.current.click();
                return;
              }
            }

            if (highlightedOption && (!searchString || searchString.trim() === '')) {
              if (handleValueChange) {
                handleValueChange(highlightedOption.value);
              }

              onInputSubmit?.(highlightedOption.value);
              if (!isControlledOpen) {
                handleOpenChange(false);
              }
              return;
            }

            if (searchString && searchString.trim() !== '') {
              onInputSubmit?.(searchString);
              if (!isControlledOpen) {
                handleOpenChange(false);
              }
              return;
            }

            setTimeout(() => {
              const selectedOption = optionRefs.find((option) => option.current?.getAttribute('aria-selected') === 'true');

              if (selectedOption?.current) {
                selectedOption.current.focus();
              } else if (!isControlledOpen) {
                handleOpenChange(false);
              }
            }, 100);

            break;
          }
          case 'ArrowDown':
          case 'ArrowUp':
            event.preventDefault();
            if (!isControlledOpen) {
              handleOpenChange(true);
            }

            setTimeout(() => {
              const selectedOption = optionRefs.find((option) => option.current?.getAttribute('aria-selected') === 'true');

              if (selectedOption?.current) {
                selectedOption.current.focus();
              } else {
                const startIndex = event.key === 'ArrowDown' && getHighlightedOption?.() ? 1 : 0;

                for (let i = startIndex; i < optionRefs.length; i++) {
                  const optionRef = optionRefs[i];
                  if (optionRef?.current) {
                    if (optionRef.current.getAttribute('data-disabled') !== 'true') {
                      optionRef.current.focus();
                      if (setHighlightedOption) {
                        setHighlightedOption(i);
                      }
                      break;
                    }
                  }
                }
              }
            }, 100);
            break;
          case 'Tab':
          case 'Escape':
            if (!isControlledOpen) {
              handleOpenChange(false);
            }
            clearHighlightedOption?.();
            break;

          default:
            if (!isControlledOpen) {
              handleOpenChange(true);
            }
            break;
        }
      }
    },
    [isEnabled, handleOpenChange, optionRefs, searchString, onInputSubmit, isControlledOpen, getHighlightedOption, clearHighlightedOption],
  );

  return {
    handleInputOnBlur,
    handleInputOnClick,
    handleInputOnClear,
    handleInputOnChange,
    handleInputOnFocus,
    handleInputOnKeyDown,
  };
}

function createAutocompleteTriggerHandlerUtils(
  isEnabled: boolean,
  inputRef: RefObject<HTMLInputElement | null>,
  handleOpenChange: (open: boolean) => void,
  onFocus?: (event: FocusEvent<HTMLButtonElement>) => void,
  isControlledOpen?: boolean,
) {
  const [internalFocused, setInternalFocused] = useState(false);

  const handleTriggerOnBlur = useCallback(
    (event: FocusEvent<HTMLButtonElement>) => {
      if (isEnabled) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    [isEnabled, internalFocused],
  );

  const handleTriggerOnClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (isEnabled) {
        event.stopPropagation();
        event.preventDefault();

        if (!isControlledOpen) {
          handleOpenChange(true);
        }

        if (inputRef.current) {
          if (event.type === 'dblclick') {
            inputRef.current.select();
          }
        }
      }
    },
    [isEnabled, handleOpenChange, inputRef, isControlledOpen],
  );

  const handleTriggerOnFocus = useCallback(
    (event: FocusEvent<HTMLButtonElement>) => {
      if (isEnabled) {
        event.preventDefault();
        event.stopPropagation();
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }

      setInternalFocused(true);
      onFocus?.(event);
    },
    [isEnabled, inputRef, onFocus],
  );

  return {
    handleTriggerOnBlur,
    handleTriggerOnClick,
    handleTriggerOnFocus,
  };
}

function createAutocompleteOptionHandlerUtils(
  isEnabled: boolean,
  options: Map<string, AutocompleteOptionType>,
  handleSearchStringChange: (searchString: string) => void,
  handleOpenChange: (open: boolean) => void,
  optionRefs: RefObject<HTMLDivElement | null>[],
  inputRef: RefObject<HTMLInputElement | null>,
  isControlledOpen?: boolean,
  setHighlightedOption?: (index: number) => void,
  closeOnSelect = true,
) {
  const handleOptionOnMouseEnter = useCallback(
    (event: MouseEvent<HTMLDivElement>, index: number) => {
      if (isEnabled) {
        event.preventDefault();
        event.stopPropagation();
        if (setHighlightedOption) {
          setHighlightedOption(index);
        }
      }
    },
    [isEnabled, setHighlightedOption, options],
  );

  const handleOptionOnClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, value: string) => {
      if (isEnabled) {
        const entry = options.get(value);

        if (entry && !entry.disabled) {
          event.preventDefault();
          event.stopPropagation();

          if (!isControlledOpen && closeOnSelect) {
            handleOpenChange(false);
          }
        }
      }
    },
    [isEnabled, options, handleSearchStringChange, handleOpenChange, isControlledOpen, closeOnSelect],
  );

  const handleOptionOnKeyDownEnter = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (!isControlledOpen && closeOnSelect) {
        handleOpenChange(false);
      }
    },
    [handleOpenChange, isControlledOpen, closeOnSelect],
  );

  const handleOptionOnKeyDownArrowDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>, optionRefs: RefObject<HTMLDivElement | null>[], index: number) => {
      event.preventDefault();
      event.stopPropagation();
      if (index === optionRefs.length - 1) {
        optionRefs[0]?.current?.focus();
        if (setHighlightedOption) {
          setHighlightedOption(0);
        }
        return;
      }
      for (let i = index + 1; i < optionRefs.length; i++) {
        if (optionRefs[i]?.current) {
          if (optionRefs[i]?.current?.getAttribute('data-disabled') !== 'true') {
            optionRefs[i]?.current?.focus();
            if (setHighlightedOption) {
              setHighlightedOption(i);
            }
            return;
          }
        }
      }
    },
    [optionRefs, setHighlightedOption],
  );

  const handleOptionOnKeyDownArrowUp = useCallback(
    (
      event: KeyboardEvent<HTMLDivElement>,
      optionRefs: RefObject<HTMLDivElement | null>[],
      index: number,
      setHighlightedOption?: (index: number) => void,
    ) => {
      event.preventDefault();
      event.stopPropagation();
      if (index === 0) {
        inputRef.current?.focus();
        if (setHighlightedOption) {
          setHighlightedOption(0);
        }
        return;
      }
      for (let i = index - 1; i >= 0; i--) {
        if (optionRefs[i]?.current) {
          if (optionRefs[i]?.current?.getAttribute('data-disabled') !== 'true') {
            optionRefs[i]?.current?.focus();
            if (setHighlightedOption) {
              setHighlightedOption(i);
            }
            return;
          }
        }
      }
    },
    [optionRefs, inputRef, setHighlightedOption],
  );

  const handleOptionOnKeyDownEscape = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  return {
    handleOptionOnClick,
    handleOptionOnMouseEnter,
    handleOptionOnKeyDownEnter,
    handleOptionOnKeyDownArrowDown,
    handleOptionOnKeyDownArrowUp,
    handleOptionOnKeyDownEscape,
  };
}

export function createAutocompleteOptionsUtils<T extends AutocompleteOptionType>() {
  const [options, setOptions] = useState<Map<string, T>>(new Map());
  const [highlightedIndex, setHighlightedIndex] = useState<number>(0);
  const optionLabelCache = useRef<Record<string, string>>({});

  const registerOption = useCallback((option: T) => {
    setOptions((prev) => {
      const newOptions = new Map(prev);
      newOptions.set(option.value, option);
      return newOptions;
    });
  }, []);

  const unregisterOption = useCallback((value: string) => {
    setOptions((prev) => {
      const newOptions = new Map(prev);
      newOptions.delete(value);
      return newOptions;
    });
  }, []);

  const optionRefs: RefObject<HTMLDivElement | null>[] = useMemo(() => {
    return Array.from(options.values()).map(() => createRef<HTMLDivElement>());
  }, [options]);

  useEffect(() => {
    if (options && options.size > 0) {
      setHighlightedIndex(0);
    }
  }, [options]);

  useEffect(() => {
    if (highlightedIndex >= options.size) {
      setHighlightedIndex(-1);
    }
  }, [options, highlightedIndex]);

  const setHighlightedOption = useCallback(
    (index: number) => {
      if (index >= 0 && index < options.size) {
        setHighlightedIndex(index);
      } else {
        setHighlightedIndex(-1);
      }
    },
    [options],
  );

  const getHighlightedOption = useCallback((): T | null => {
    const optionValues = Array.from(options.values());
    return highlightedIndex >= 0 && highlightedIndex < optionValues.length ? (optionValues[highlightedIndex] ?? null) : null;
  }, [options, highlightedIndex]);

  const clearHighlightedOption = useCallback(() => {
    setHighlightedIndex(-1);
  }, []);

  return {
    options,
    setOptions,
    registerOption,
    unregisterOption,
    optionRefs,
    optionLabelCache,
    highlightedIndex,
    setHighlightedOption,
    getHighlightedOption,
    clearHighlightedOption,
  };
}

export function useAutocomplete<T extends AutocompleteOptionType>({
  allowFreeText,
  closeOnSelect = true,
  isEnabled,
  inputRef,
  inputValue,
  onBlur,
  onFocus,
  onInputChange,
  onInputSubmit,
  onOpenChange,
  open: controlledOpen,
}: {
  allowFreeText?: boolean;
  /** Whether selecting an option should close the popover. Defaults to `true` (single-select behavior); set `false` for multi-select consumers that should stay open across selections. */
  closeOnSelect?: boolean;
  isEnabled: boolean;
  inputRef: RefObject<HTMLInputElement | null>;
  inputValue?: string;
  onBlur?: (event: FocusEvent<HTMLButtonElement>) => void;
  onFocus?: (event: FocusEvent<HTMLButtonElement>) => void;
  onInputChange?: (value: string) => void;
  onInputSubmit?: (searchString: string) => void;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
}) {
  const openUtils = createAutocompleteOpenUtils(isEnabled, onOpenChange, inputRef, onBlur, controlledOpen);
  const optionsUtils = createAutocompleteOptionsUtils<T>();
  const autocompleteSearchUtils = createAutocompleteSearchUtils({
    allowFreeText,
    inputValue,
    isEnabled,
    onInputChange,
    options: optionsUtils.options,
  });
  const optionsHandlerUtils = createAutocompleteOptionHandlerUtils(
    isEnabled,
    optionsUtils.options,
    autocompleteSearchUtils.handleSearchStringChange,
    openUtils.handleOpenChange,
    optionsUtils.optionRefs,
    inputRef,
    openUtils.isControlledOpen,
    optionsUtils.setHighlightedOption,
    closeOnSelect,
  );
  const inputHandlerUtils = createAutocompleteInputHandlerUtils(
    isEnabled,
    openUtils.handleOpenChange,
    autocompleteSearchUtils.handleSearchStringChange,
    optionsUtils.optionRefs,
    openUtils.open,
    onBlur,
    onFocus,
    onInputSubmit,
    autocompleteSearchUtils.searchString,
    openUtils.isControlledOpen,
    optionsUtils.setHighlightedOption,
    optionsUtils.getHighlightedOption,
    optionsUtils.clearHighlightedOption,
  );
  const triggerHandlerUtils = createAutocompleteTriggerHandlerUtils(
    isEnabled,
    inputRef,
    openUtils.handleOpenChange,
    onFocus,
    openUtils.isControlledOpen,
  );

  return {
    ...openUtils,
    ...optionsUtils,
    ...autocompleteSearchUtils,
    ...optionsHandlerUtils,
    ...inputHandlerUtils,
    ...triggerHandlerUtils,
  };
}

export function updateLabelCache(
  options: Map<string, AutocompleteOptionType>,
  optionLabelCache: RefObject<Record<string, string>>,
  values: string[] | string | undefined,
) {
  const newCache: Record<string, string> = { ...optionLabelCache.current };

  Array.from(options.values()).forEach((option) => {
    if (option.label) {
      newCache[option.value] = option.label;
    }
  });

  const valueArray = Array.isArray(values) ? values : values ? [values] : [];

  valueArray.forEach((value) => {
    if (!newCache[value]) {
      const optionElements = Array.from(document.querySelectorAll(`[data-value="${value}"]`));
      if (optionElements.length > 0 && optionElements[0] && optionElements[0].textContent) {
        newCache[value] = optionElements[0].textContent;
      } else {
        newCache[value] = value;
      }
    }
  });

  optionLabelCache.current = newCache;
}

/**
 * Splits a label string into parts based on a search string match, enabling text highlighting.
 * @param label - The text to search within and split
 * @param searchString - The search term to match against (case-insensitive)
 * @returns Array of string parts where matching segments can be highlighted
 */
export function highlightLabel(label: string, searchString?: string) {
  const searchStr = searchString?.toLowerCase().trim();
  if (!searchStr) return [label];
  const escapeRegExp = (str: string) => {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };
  const regex = new RegExp(`(${escapeRegExp(searchStr)})`, 'gi');
  return label.split(regex);
}
